import { useEffect, useState } from "react";
import { Copy, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function DiditSettings() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ enabled: false, workflow_id: "", api_key: "", webhook_secret: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/settings/didit").then(({ data }) => {
      if (cancelled) return;
      setConfig(data);
      setForm((current) => ({ ...current, enabled: data.enabled, workflow_id: data.workflow_id || "" }));
    }).catch((err) => { if (!cancelled) setError(formatApiError(err)); });
    return () => { cancelled = true; };
  }, []);
  const save = async (event) => {
    event.preventDefault();
    setError("");
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/didit", form);
      setConfig(data);
      setForm({ enabled: data.enabled, workflow_id: data.workflow_id, api_key: "", webhook_secret: "" });
      toast.success(data.enabled ? "Didit automatic verification enabled." : "Didit connection saved.");
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <section className="mb-5 max-w-2xl rounded-lg border border-sky-400/25 bg-[#101010] p-5 sm:p-6">
      <div className="mb-4 flex items-start gap-3">
        <ShieldCheck className="mt-1 h-6 w-6 shrink-0 text-sky-300" />
        <div>
          <h2 className="text-lg font-semibold text-white">Didit automatic ID verification</h2>
          <p className="mt-1 text-sm text-slate-400">Connect a live Didit workflow with ID front and back, selfie liveness, face match, and duplicate checks. Phone verification stays required.</p>
        </div>
      </div>
      <a href="https://business.didit.me" target="_blank" rel="noreferrer" className="mb-4 inline-flex items-center gap-2 text-sm text-sky-300">Open Didit console <ExternalLink className="h-4 w-4" /></a>
      <form onSubmit={save} className="space-y-4">
        <label className="block text-sm text-slate-300">Workflow ID
          <input value={form.workflow_id} onChange={(e) => update("workflow_id", e.target.value)} placeholder="Workflow UUID from Didit" className="mt-2 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-white" />
        </label>
        <label className="block text-sm text-slate-300">API key {config?.api_key_set && <span className="text-emerald-300">— saved</span>}
          <input type="password" autoComplete="new-password" value={form.api_key} onChange={(e) => update("api_key", e.target.value)} placeholder={config?.api_key_set ? "Leave blank to keep saved key" : "Didit API key"} className="mt-2 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-white" />
        </label>
        <div className="rounded-lg border border-white/10 bg-black/20 p-3 text-sm text-slate-300">
          <p>Add this webhook URL in Didit. Choose V3 and subscribe to <strong>status.updated</strong> and <strong>data.updated</strong>.</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 break-all text-xs text-sky-200">{config?.webhook_url || "Loading webhook URL..."}</code>
            <button type="button" disabled={!config?.webhook_url} aria-label="Copy webhook URL" onClick={async () => { try { await navigator.clipboard.writeText(config.webhook_url); toast.success("Webhook URL copied."); } catch { toast.error("Please copy the displayed URL."); } }} className="rounded p-2 hover:bg-white/10"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
        <label className="block text-sm text-slate-300">Webhook signing secret {config?.webhook_secret_set && <span className="text-emerald-300">— saved</span>}
          <input type="password" autoComplete="new-password" value={form.webhook_secret} onChange={(e) => update("webhook_secret", e.target.value)} placeholder={config?.webhook_secret_set ? "Leave blank to keep saved secret" : "Secret from the Didit webhook destination"} className="mt-2 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-white" />
        </label>
        <label className="flex items-center gap-3 text-sm text-white"><input type="checkbox" checked={form.enabled} onChange={(e) => update("enabled", e.target.checked)} /> Enable Didit verification for members</label>
        {error && <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</p>}
        <button disabled={saving || !config} className="rounded-full bg-sky-300 px-5 py-2.5 font-bold text-black disabled:opacity-50">{saving ? "Checking connection..." : "Save Didit connection"}</button>
      </form>
    </section>
  );
}
